/**
 * Shared ApiResponse<T> unwrap helpers.
 *
 * Extracted from frontend/src/services/api.ts so that future per-domain
 * splits (api/cwpp.ts, api/ciem.ts, etc.) can `import { _unwrap, _unwrapWithOpts,
 * _thenUnwrap } from './_unwrap'` instead of round-tripping through the
 * monolithic `../api`. The cspm.ts split (already real) should also adopt
 * these in a follow-up to drop its local copy if any.
 *
 * The semantics mirror legacy fetchClient behavior: a non-error response returns
 * `data`; an error response throws an Error with `.status` set so call
 * sites using `try/catch` continue to work unchanged.
 */
import type { ApiResponse } from './client';

/** Unwrap an ApiResponse<T> from the fetch client. Throws on error. */
export function _unwrap<T>(resp: ApiResponse<T>): T {
  if (resp.error) {
    const err = new Error(resp.error) as Error & { status?: number };
    err.status = resp.status;
    throw err;
  }
  return resp.data as T;
}

/**
 * Like _unwrap, but accepts an in-flight Promise<ApiResponse<T>> so callers
 * passing RequestOptions (custom headers, params, body via fetchClient.request)
 * can await + unwrap in one step.
 *
 * Usage:
 *   return _unwrapWithOpts(fetchClient.request<X>('/api/v1/scim/Users', {
 *     method: 'GET',
 *     headers: { Authorization: `Bearer ${token}` },
 *   }));
 */
export async function _unwrapWithOpts<T>(p: Promise<ApiResponse<T>>): Promise<T> {
  const resp = await p;
  if (resp.error) {
    const err = new Error(resp.error) as Error & { status?: number };
    err.status = resp.status;
    throw err;
  }
  return resp.data as T;
}

/**
 * Helper for inline `.then(_thenUnwrap)` chains — used by arrow-function API
 * methods that haven't been rewritten to async/await form. Returns `any` to
 * match legacy fetchClient `r.data` behavior so existing callers don't need generic
 * annotations.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function _thenUnwrap(resp: ApiResponse<any>): any {
  if (resp.error) {
    const err = new Error(resp.error) as Error & { status?: number };
    err.status = resp.status;
    throw err;
  }
  return resp.data;
}
